import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {httpGetConversations, HttpGetConversationsOptions} from '../http/conversations.http'
import {QUERY_KEYS} from '@/queryClient'
import {ConversationsList} from '@/features/conversations/types'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

/**
 * Hook to fetch conversations with pagination
 * @param patientId ID of the patient to get conversations for
 * @param perPage Number of results per page (defaults to 30)
 * @param search Optional search query to filter conversations
 * @param options Additional query options
 */
export const useConversations = (
    patientId: string,
    perPage: number = 30,
    search?: string,
    entityType?: HttpGetConversationsOptions['params']['entityType'],
    options?: Pick<UseInfiniteQueryOptions<ConversationsList>, 'enabled'>
) => {
    // Only include search in query key when it's actually used
    const normalizedSearch = search && search.trim() ? search.trim() : undefined

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.CONVERSATIONS, patientId, perPage, normalizedSearch, entityType],
        queryFn: ({pageParam = 1}) =>
            httpGetConversations({
                params: {
                    patientId,
                    entityType,
                    page: pageParam,
                    limit: perPage,
                    ...(normalizedSearch && {search: normalizedSearch})
                }
            }),
        initialPageParam: 1,
        enabled: !!patientId,
        ...options,
        getNextPageParam: lastPage => {
            const meta = lastPage.data.meta
            return meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null
        }
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        conversations: query.data?.pages.flatMap(page => page.data.data) || [],
        entityType
    }
}
